import React, { memo, useEffect, useState } from "react";
import {
  Card,
  Col,
  Flex,
  Image,
  Popover,
  Row,
  Slider,
  Typography,
  InputNumber,
  Alert,
} from "antd";
import ReturnsImage from "../../../assets/images/returns.png";
import MaturityImage from "../../../assets/icons/maturity.svg";
import DealValueImage from "../../../assets/images/dealValue.png";
import CustomButton from "../../../components/CustomButton/Button";
import { useNavigate } from "react-router-dom";
import {
  DownOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import SoldoutCkeck from "../../../assets/images/soldout-check.png";
import MoneyPlant from "../../../assets/images/money-plant.png";
import AccountDetails from "../../../components/AccountDetails/AccountDetails";
import CustomModal from "../../../components/CustomModal/CustomModal";
import { useSelector } from "react-redux";
import {
  DateFormatter,
  formatDateLong,
  dealsClosesIn,
  formatCurrency,
  formatToLacCr,
  calculateSoldOutPercentage,
  calculateReturns,
  calculateDiscountAmount,
  calculateAvailableAmountToInvest,
  calculateNetReturns,
  calculatePlatformFee,
  calculateExpectedNetProfit,
  calculateExpectedNetRepayment,
} from "../../../helpers/helpers";
import InvestmentBreakdownRow from "../../../components/LiveDeals/InvestmentBreakdownRow";
import dayjs from "dayjs";
import { GLOBAL_CONSTANTS } from "../../../constants/constants";
import JvDealBuyButtons from "./JvDealBuyButtons";

const { Title, Text } = Typography;

// same helpers as DealDetails
const MIN_DEAL_AMOUNT = 200000; // 2L
const roundAmount = (num) => Math.round(Number(num || 0));
const parsePercentValue = (rawValue) => {
  if (rawValue === undefined || rawValue === null || rawValue === "")
    return null;
  const normalized = String(rawValue)
    .replace(/[%\s,]/g, "")
    .trim();
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const DealsCard = ({ deal, selectedTab, interestStatus }) => {
  const accBalance = useSelector((state) => state.manageFunds.accBalance);
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [isSubtitleExpanded, setIsSubtitleExpanded] = useState(false);
  const [addBalanceOpen, setAddBalanceOpen] = useState(false);
  const [yourDealError, setYourDealError] = useState("");

  const [yourDealAmount, setYourDealAmmount] = useState(0);

  // invest-now state + bounds
  const [amountToInvestNow, setAmountToInvestNow] = useState(0);
  const [minInvestNow, setMinInvestNow] = useState(0);
  const [maxInvestNow, setMaxInvestNow] = useState(0);
  const [isAmountDisabled, setIsAmountDisabled] = useState(false);

  const [amountError, setAmountError] = useState("");
  const [intrestModal, setIntrestModal] = useState({
    open: false,
    option: "",
  });
  const navigate = useNavigate();

  const handlePopoverClose = () => {
    setPopoverVisible(false);
  };

  // deal-level derived values
  const soldOutPercentage = calculateSoldOutPercentage(
    deal?.fundedAmt,
    deal?.approvedFundAmt,
  );
  const returns = calculateReturns(deal?.annualDiscountRate);

  const isJointVenturesDeal = deal?.dealCategoryId === "DCT1000002";
  const isInvoiceFinancingDeal = deal?.dealCategoryId === "DCT1000001";
  const isSupplyChainDeal = deal?.dealCategoryId === "DCT1000006";
  const isInvoiceStyleDeal = isInvoiceFinancingDeal || isSupplyChainDeal;

  // Determine minimum deal amount based on deal type
  const apiMinDealAmount = Number(
    (deal?.minDealAmt ??
      deal?.minInvestmentCriteria ??
      deal?.minimumDealAmount) ||
      0,
  );

  const resolvedRoiValue = (() => {
    const rawRoi = deal?.jvDealExpectedROI ?? deal?.expectedROI;
    const parsedRoi = parsePercentValue(rawRoi);
    if (!Number.isFinite(parsedRoi)) return returns;
    const normalizedRoi = parsedRoi <= 1 ? parsedRoi * 100 : parsedRoi;
    return Number(normalizedRoi.toFixed(2));
  })();
  const resolvedProfitShareValue = (() => {
    const rawProfitShare = deal?.profitShare ?? deal?.jvDealProfitShare;
    const parsedProfitShare = parsePercentValue(rawProfitShare);
    if (!Number.isFinite(parsedProfitShare)) return null;
    const normalizedProfitShare =
      parsedProfitShare <= 1 ? parsedProfitShare * 100 : parsedProfitShare;
    return Number(normalizedProfitShare.toFixed(2));
  })();
  const availableAmountToInvest = Number(
    calculateAvailableAmountToInvest(
      deal?.approvedFundAmt || 0,
      deal?.fundedAmt || 0,
    ),
  );
  const displayedAvailableAmount = availableAmountToInvest;
  const validateYourDealAmount = (dealAmt) => {
    const available = Number(availableAmountToInvest || 0);
    const minRequired = minDealAmountForThisDeal;

    if (Number.isNaN(dealAmt)) return;
    if (available >= minRequired && Math.round(dealAmt) < minRequired) {
      setYourDealError(
        `Your Deal Amount cannot be less than ₹ ${minRequired.toLocaleString("en-IN")}`,
      );
    } else if (Math.round(dealAmt) > available) {
      setYourDealError(
        `Your Deal Amount cannot be more than ₹ ${available.toLocaleString("en-IN")}`,
      );
    } else {
      setYourDealError("");
    }
  };

  const validApiMinDealAmount =
    Number.isFinite(apiMinDealAmount) && apiMinDealAmount > 0
      ? apiMinDealAmount
      : MIN_DEAL_AMOUNT;
  const minDealAmountForThisDeal = isJointVenturesDeal
    ? validApiMinDealAmount
    : MIN_DEAL_AMOUNT;

  // initialize min/max + defaults when deal loads
  useEffect(() => {
    if (isInvoiceStyleDeal && (!deal?.annualDiscountRate || !deal?.tenure))
      return;

    let minDealAmt;
    let maxDealAmt;
    let initialDealAmt;
    let disableInput;

    if (isInvoiceStyleDeal) {
      minDealAmt = availableAmountToInvest;
      maxDealAmt = availableAmountToInvest;
      initialDealAmt = availableAmountToInvest;
      disableInput = true;
    } else {
      const maxNowByAvailable = availableAmountToInvest;
      const isRemainingBelowMinInvestNow =
        maxNowByAvailable < minDealAmountForThisDeal;

      if (!isRemainingBelowMinInvestNow) {
        minDealAmt = minDealAmountForThisDeal;
        maxDealAmt = availableAmountToInvest;
        disableInput = false;

        initialDealAmt = Math.min(Math.max(maxDealAmt, minDealAmt), maxDealAmt);
      } else {
        minDealAmt = availableAmountToInvest;
        maxDealAmt = availableAmountToInvest;
        initialDealAmt = availableAmountToInvest;
        disableInput = true;
      }
    }

    setIsAmountDisabled(disableInput);
    setYourDealAmmount(initialDealAmt);

    // 3) convert to invest-now min/max
    setMinInvestNow(minDealAmt);
    setMaxInvestNow(maxDealAmt);

    // 4) initial invest-now
    setAmountToInvestNow(initialDealAmt);
    setAmountError("");
    validateYourDealAmount(initialDealAmt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal, minDealAmountForThisDeal]); // only when deal or minDealAmountForThisDeal changes

  const minAmount = roundAmount(minInvestNow);
  const maxAmount = roundAmount(maxInvestNow);
  const isSliderDisabled = isAmountDisabled || maxAmount <= minAmount;

  // amounts derived from current selection
  const discountAmount = calculateDiscountAmount(
    yourDealAmount,
    deal?.annualDiscountRate,
    deal?.tenure,
  );

  const jvRoiAmount = isJointVenturesDeal
    ? (Number(amountToInvestNow || 0) * Number(resolvedRoiValue || 0)) / 100
    : 0;
  const jvKarncyCarryAmount = isJointVenturesDeal
    ? (jvRoiAmount * Number(GLOBAL_CONSTANTS.JV_KARNCY_CARRY_PERCENT || 0)) /
      100
    : 0;
  const expectedGrossProfit = isJointVenturesDeal
    ? jvRoiAmount
    : discountAmount;
  const platformFee = isJointVenturesDeal
    ? jvKarncyCarryAmount
    : calculatePlatformFee(yourDealAmount, deal?.platformFee, deal?.tenure);
  const netReturns = calculateNetReturns(
    discountAmount,
    platformFee,
    deal?.penalCharge,
  );
  const expectedNetProfit = isJointVenturesDeal
    ? expectedGrossProfit - platformFee
    : calculateExpectedNetProfit(discountAmount, platformFee);
  const expectedNetRepayment = isJointVenturesDeal
    ? Number(amountToInvestNow || 0) + Number(expectedGrossProfit || 0)
    : calculateExpectedNetRepayment(
        amountToInvestNow,
        discountAmount,
        platformFee,
      );

  const netReturnsForOrderSummary = isJointVenturesDeal
    ? expectedGrossProfit
    : netReturns;
  const primaryRateLabel = isJointVenturesDeal
    ? "Expected ROI"
    : "Interest Rate";
  const primaryRateValue = isJointVenturesDeal ? resolvedRoiValue : returns;
  const investmentBreakdownRows = [
    {
      label: "Expected Margin",
      tooltip: "Gross profit is the profit before platform fee and taxes.",
      value: expectedGrossProfit,
    },
    ...(!isJointVenturesDeal
      ? [
          {
            label: "Platform Fee",
            tooltip:
              "The platform fee is charged for the service provided and is inclusive of GST.",
            value: platformFee,
          },
          {
            label: "Expected Net Margin",
            tooltip:
              "Net profit is the amount after deducting platform fee and other charges.",
            value: expectedNetProfit,
          },
        ]
      : []),
    {
      label: "Total Payout",
      tooltip: isJointVenturesDeal
        ? "Total payout is the sum of amount to invest now and expected margin."
        : "Net repayment is the total amount that will be returned after settlement.",
      value: expectedNetRepayment,
    },
  ];

  const isBuyNowDisabled =
    Number(yourDealAmount) > Number(accBalance?.amount) ||
    !!yourDealError ||
    !!amountError;

  const handleAmountToInvestChange = (val) => {
    // val can be: "" (user cleared), string of digits, or a number (depending on parser)
    if (val === null || val === undefined || val === "") {
      // allow clearing — keep empty state so user can type
      setAmountToInvestNow("");
      setYourDealAmmount(0);
      setAmountError("");
      return;
    }

    // normalize to numeric digits only (safe)
    const raw = String(val).replace(/[^\d]/g, "");
    const num = raw === "" ? 0 : Number(raw);

    if (Number.isNaN(num)) {
      setAmountError("Please enter a valid amount.");
      return;
    }

    // validation (show error but DO NOT clamp the input)
    const roundedMin = Math.round(minAmount);
    const roundedMax = Math.round(maxAmount);
    if (num < roundedMin) {
      setAmountError(
        `Amount cannot be less than ₹ ${roundedMin.toLocaleString("en-IN")}`,
      );
    } else if (num > roundedMax) {
      setAmountError(
        `Amount cannot be more than ₹ ${roundedMax.toLocaleString("en-IN")}`,
      );
    } else {
      setAmountError("");
    }

    // update state with the exact number typed by user
    setAmountToInvestNow(num);

    // update yourDealAmount using your helper
    setYourDealAmmount(num);
    validateYourDealAmount(num);
  };

  const handleSliderChange = (value) => {
    const num = Number(value) || 0;
    setAmountError(""); // slider always within range
    setAmountToInvestNow(num);

    setYourDealAmmount(num);
    validateYourDealAmount(num);
  };

  const clampedSliderValue = Math.min(
    Math.max(amountToInvestNow || 0, Number(minAmount)),
    Number(maxAmount),
  );

  const handleBuyNow = () => {
    const data = {
      dealId: deal?.dealId,
      entName: deal?.entName || deal?.dealName || deal?.businessName,
      netReturns: netReturnsForOrderSummary,
      dealValue: deal?.approvedFundAmt,
      repaymentOn: dayjs(deal?.dealExpiryDate)
        .add(deal?.tenure, "day")
        .format("YYYY-MM-DD"),
      maturity: deal?.tenure,
      expectedGrossProfit: expectedGrossProfit,
      platformFee: platformFee,
      expectedNetYield: isJointVenturesDeal
        ? expectedGrossProfit
        : expectedNetProfit,
      expectedNetRepayment: expectedNetRepayment,
      amountToPayNow: amountToInvestNow,
      dealAmt: yourDealAmount.toString(),
      orgId: deal.orgId || deal.dealOrgId,
      busEntId: deal.busEntId,
      isJointVenturesDeal,
    };
    navigate("/order-summary", { state: { dealInfo: data } });
  };

  const dealCategoryName = deal?.dealCategoryName || "";
  const jvDealName = deal?.dealName || "";
  const subtitleText = [
    deal?.lineOfBusinessName,
    deal?.rating ? `${deal.rating} enterprise` : null,
    deal?.orgName,
  ]
    .filter(Boolean)
    .join(" • ");
  const hasLongSubtitle = subtitleText.length > 100;
  const displayedSubtitle =
    hasLongSubtitle && !isSubtitleExpanded
      ? `${subtitleText.slice(0, 100)}...`
      : subtitleText;

  const popoverContent = (
    <div style={{ width: 300 }}>
      <Flex align="center" justify="space-between" style={{ marginBottom: 10 }}>
        <Title level={5}>Amount Breakup</Title>
        <CloseCircleOutlined
          style={{ fontSize: 24, cursor: "pointer" }}
          onClick={handlePopoverClose}
        />
      </Flex>
      <Flex vertical gap="large">
        {investmentBreakdownRows.map((row) => (
          <InvestmentBreakdownRow key={row.label} {...row} />
        ))}
      </Flex>
    </div>
  );

  return (
    <Card className="deal-card">
      <Row>
        <Col xs={24} lg={11}>
          <div className="deal-header-row">
            <div
              className={`deal-branding ${isJointVenturesDeal ? "no-logo" : ""}`}
            >
              {!isJointVenturesDeal ? (
                deal?.entLogo ? (
                  <img
                    src={deal.entLogo}
                    className="deal-logo"
                    alt="Enterprise Logo"
                  />
                ) : (
                  <div className="deal-logo-placeholder">
                    {String(
                      deal?.entName ||
                        deal?.dealName ||
                        deal?.businessName ||
                        "D",
                    )
                      .charAt(0)
                      .toUpperCase()}
                  </div>
                )
              ) : null}
              <div className="deal-header-copy">
                <Title level={4} className="deal-title">
                  {isJointVenturesDeal
                    ? jvDealName || deal?.entName || "N/A"
                    : deal?.entName ||
                      deal?.dealName ||
                      deal?.businessName ||
                      "N/A"}
                </Title>
                <Typography className="deal-subtitle">
                  {displayedSubtitle}
                  {hasLongSubtitle ? (
                    <CustomButton
                      type="link"
                      style={{
                        padding: 0,
                        marginLeft: 6,
                        fontSize: 12,
                        height: "auto",
                      }}
                      onClick={() =>
                        setIsSubtitleExpanded((prevState) => !prevState)
                      }
                    >
                      {isSubtitleExpanded ? "Read Less" : "Read More"}
                    </CustomButton>
                  ) : null}
                </Typography>
                <Flex
                  justify="space-between"
                  align="center"
                  style={{ gap: 10, width: "100%" }}
                >
                  <div className="sold-out-tag">
                    <span>{`${soldOutPercentage}% Sold Out`}</span>
                  </div>
                  {dealCategoryName ? (
                    <Typography className="deal-chip">
                      {dealCategoryName}
                    </Typography>
                  ) : null}
                </Flex>
              </div>
            </div>
          </div>

          <div className="deal-constraint-container">
            <div className="deal-constraint">
              <Image src={ReturnsImage} preview={false} alt="Interest Rate" />
              <div className="deal-constraint-details">
                <Typography style={{ fontSize: 13 }}>
                  {primaryRateLabel}
                </Typography>
                <Title level={5}>{primaryRateValue}%</Title>
              </div>
            </div>
            {isJointVenturesDeal ? (
              <div className="deal-constraint">
                <Image src={ReturnsImage} preview={false} alt="Profit Share" />
                <div className="deal-constraint-details">
                  <Typography style={{ fontSize: 13 }}>Profit Share</Typography>
                  <Title level={5}>
                    {resolvedProfitShareValue !== null
                      ? `${resolvedProfitShareValue}%`
                      : "N/A"}
                  </Title>
                </div>
              </div>
            ) : null}
            {!isJointVenturesDeal ? (
              <div className="deal-constraint">
                <Image src={MaturityImage} preview={false} alt="Maturity" />
                <div className="deal-constraint-details">
                  <Typography style={{ fontSize: 13 }}>Maturity</Typography>
                  <Title level={5}>{deal?.tenure || "N/A"} days</Title>
                </div>
              </div>
            ) : null}
            <div className="deal-constraint">
              <Image src={DealValueImage} preview={false} alt="Deal Value" />
              <div className="deal-constraint-details">
                <Typography style={{ fontSize: 13 }}>Deal Value</Typography>
                <Title level={5}>
                  ₹ {formatToLacCr(Number(deal?.approvedFundAmt))}
                </Title>
              </div>
            </div>
          </div>

          <Flex justify="center" style={{ marginTop: 20 }}>
            <CustomButton
              size="small"
              variant="text"
              color="primary"
              className="view-details-btn"
              onClick={() =>
                navigate("/deal-details", {
                  state: {
                    dealId: deal?.dealId,
                    enteredDealAmount: yourDealAmount,
                    options: interestStatus
                  },
                })
              }
            >
              View Deal Details
            </CustomButton>
          </Flex>
        </Col>

        <Col xs={0} lg={2} className="divider-container">
          <div className="divider" />
        </Col>

        {selectedTab === "approved" && (
          <Col xs={24} lg={11} className="ammount-calculation-container">
            {!isJointVenturesDeal ? (
              <Flex
                align="center"
                justify="space-between"
                style={{ marginBottom: 30 }}
              >
                <Typography style={{ color: "#40566D" }}>
                  Available Amount to Invest
                </Typography>
                <Typography style={{ color: "#40566D", fontWeight: 600 }}>
                  ₹ {formatToLacCr(Number(displayedAvailableAmount))}
                </Typography>
              </Flex>
            ) : null}

            {isInvoiceStyleDeal && (
              <Alert
                type="warning"
                showIcon
                message="Cannot customize amount. You need to fund the deal in full."
                style={{
                  marginBottom: 16,
                  borderRadius: 8,
                  fontSize: 13,
                }}
              />
            )}

            {isJointVenturesDeal && (
              <Flex align="center" justify="space-between">
                <Typography
                  style={{ color: "#40566D", fontSize: 14, fontWeight: 600 }}
                >
                  Amount to Invest Now
                </Typography>

                {/* Input - allow free typing, no min/max prop */}
                <InputNumber
                  changeOnWheel={false}
                  value={
                    amountToInvestNow === "" ? undefined : amountToInvestNow
                  }
                  precision={0}
                  inputMode="numeric"
                  disabled={
                    isAmountDisabled || isInvoiceStyleDeal || isSliderDisabled
                  }
                  formatter={(v) =>
                    // show empty while user clears
                    v === undefined || v === ""
                      ? ""
                      : new Intl.NumberFormat("en-IN").format(Number(v))
                  }
                  parser={(v) => (v || "").replace(/[^\d]/g, "")} // keep only digits
                  onChange={(val) =>
                    handleAmountToInvestChange(val === null ? "" : val)
                  }
                  onKeyDown={(e) => {
                    const allowed = [
                      "Backspace",
                      "Delete",
                      "Tab",
                      "ArrowLeft",
                      "ArrowRight",
                      "Home",
                      "End",
                    ];
                    if (allowed.includes(e.key) || e.ctrlKey || e.metaKey)
                      return;
                    if (!/^\d$/.test(e.key)) e.preventDefault();
                  }}
                  onPaste={(e) => {
                    const text = e.clipboardData.getData("text");
                    if (!/^\d+$/.test(text)) e.preventDefault();
                  }}
                  size="large"
                  prefix="₹"
                  variant="borderless"
                  className="number-input"
                />
              </Flex>
            )}

            {amountError && (
              <Typography
                style={{ fontSize: 12, color: "#750A0A", marginTop: 5 }}
              >
                {amountError}
              </Typography>
            )}

            {isJointVenturesDeal && (
              <Slider
                min={Number(minAmount)}
                max={Number(maxAmount)}
                value={clampedSliderValue}
                onChange={(value) => {
                  handleSliderChange(value); // will set amountToInvestNow to a clamped valid number
                  setAmountError("");
                }}
                step={isSliderDisabled ? 1 : 5000}
                disabled={isSliderDisabled || isInvoiceStyleDeal}
              />
            )}

            {Number(amountToInvestNow) > Number(accBalance.amount) && (
              <Flex justify="flex-end" style={{ marginTop: 10 }}>
                <Typography style={{ fontSize: 12, color: "#750A0A" }}>
                  Insufficient balance in wallet.
                  <Text
                    onClick={() => setAddBalanceOpen(true)}
                    style={{
                      cursor: "pointer",
                      fontSize: 12,
                      color: "#750A0A",
                      marginLeft: 4,
                    }}
                    strong
                  >
                    Add Money
                  </Text>
                </Typography>
                <CustomModal
                  open={addBalanceOpen}
                  centered
                  onCancel={() => setAddBalanceOpen(false)}
                  content={<AccountDetails />}
                />
              </Flex>
            )}

            <Flex
              align="center"
              justify="space-between"
              style={{ marginBottom: 20 }}
            >
              <Popover
                content={popoverContent}
                trigger="click"
                placement="bottom"
                open={popoverVisible}
                onOpenChange={(visible) => setPopoverVisible(visible)}
              >
                <Flex gap="small">
                  <Typography style={{ color: "#40566D", fontSize: 12 }}>
                    Your Deal Amount
                  </Typography>
                  <DownOutlined style={{ color: "#40566D", fontSize: 12 }} />
                </Flex>
              </Popover>
              <Typography style={{ fontSize: 14, fontWeight: 600 }}>
                {formatCurrency(yourDealAmount)}
              </Typography>
            </Flex>
            {yourDealError && (
              <Typography
                style={{ fontSize: 12, color: "#750A0A", marginBottom: 6 }}
              >
                {yourDealError}
              </Typography>
            )}
            <Flex
              align="center"
              justify="space-between"
              style={{ marginBottom: 20 }}
            >
              <Typography style={{ color: "#40566D", fontSize: 12 }}>
                Your Net Returns
              </Typography>
              <Typography style={{ fontSize: 14, fontWeight: 600 }}>
                {formatCurrency(netReturnsForOrderSummary)}
              </Typography>
            </Flex>

            {isJointVenturesDeal ? (
              <JvDealBuyButtons
                options={interestStatus}
                dealId={deal?.dealId}
                fromDealsCard={true}
                intrestModal={intrestModal}
                setIntrestModal={setIntrestModal}
              />
            ) : (
              <CustomButton
                type="primary"
                disabled={isBuyNowDisabled}
                onClick={handleBuyNow}
                style={{ backgroundColor: "#00874F", marginBottom: 10 }}
                block
              >
                Buy Now
              </CustomButton>
            )}

            {isInvoiceStyleDeal ? (
              <Flex gap="small" align="center" justify="center">
                <ClockCircleOutlined
                  style={{ color: "#40566D", fontSize: 12 }}
                />
                <Typography style={{ color: "#40566D", fontSize: 12 }}>
                  Closes in {dealsClosesIn(deal?.dealExpiryDate)}
                </Typography>
              </Flex>
            ) : null}
          </Col>
        )}

        {selectedTab === "agreement" && (
          <Col xs={24} lg={11}>
            <div className="soldout-section-container">
              <Image preview={false} src={SoldoutCkeck} alt="Check logo" />
              <Typography
                style={{ color: "#101924", fontWeight: 600, fontSize: 20 }}
              >
                {`${deal?.invCount} investor${
                  deal?.invCount > 1 ? "s" : ""
                } have invested in this deal`}
              </Typography>
              <Typography
                style={{ color: "#40566D", fontSize: 16, fontWeight: 600 }}
              >
                Sold out on{" "}
                {deal?.soldOutAt
                  ? DateFormatter(deal.soldOutAt)
                  : formatDateLong(deal?.fundedDate)}
              </Typography>
            </div>
          </Col>
        )}

        {selectedTab === "settled" && (
          <Col xs={24} lg={11}>
            <div className="soldout-section-container">
              <Image preview={false} src={MoneyPlant} alt="Check logo" />
              <Typography style={{ color: "#40566D" }}>
                {`${deal?.invCount} investor${
                  deal?.invCount > 1 ? "s" : ""
                } received`}
              </Typography>
              <Typography
                style={{ color: "#101924", fontWeight: 600, fontSize: 20 }}
              >
                {`total returns of ${formatToLacCr(deal?.investorPayout)}`}
              </Typography>
            </div>
          </Col>
        )}
      </Row>
    </Card>
  );
};

export default memo(DealsCard);
